import { OffHireType } from '../../graphql';
import type {
  AssortmentExclusionId,
  ChannelCostEstimateId,
  CoaContractId,
  LoadingUnit,
  OrganizationId,
  PortCostDefaultId,
  ScenarioTimeline_PortCallFragment,
  ShipId,
} from '../../graphql';
import { VesselArea, VesselSpeedTag, VesselSpeedType } from '../../graphql';
import type { ScenarioLocation, ScenarioOrder } from '../../lib/planningData';
import type { AssortmentExclusionItem } from '../hooks/useAssortmentExclusions';
import type { ClientId } from './ClientId';
import type { Vessel } from './Vessel';
import type {
  Constraint,
  PortCallEntry,
  CoaEntry,
  OffHireEntry,
  SingleEntry,
  SpotEntry,
  TCOrderEntry,
  CargoEntry,
  Entry,
  TcInEntry,
  LocationId,
} from '@seaber/common/core/entries';
import { EventCategory } from '@seaber/common/core/entries';
import type { NewCargoAllocation } from './Core.cargo-allocation';
import { filteredMap, NONE } from '../../lib/util';
import type { TCScheduleEntry } from '../Scenario';
import { z } from 'zod';

export type TimeData<EventType extends Events = Events> = {
  /** Location of a vessel's route relative to an Emission Control Area (ECA) */
  area: VesselArea;
  isFirstEvent: boolean;
  isLastEvent: boolean;
  /** First "dynamic" event, i.e. non-offhire which have static start&end times */
  isFirstShipmentEvent?: boolean;
  hasMiddle: boolean;
  canPortCallSwapLeft: boolean;
  canPortCallSwapRight: boolean;
  index: number;
  start: number;
  calculatedDuration: number;
  work: CalculatedWork | undefined;
  arriveInPort: number;
  /** Start time of the first load/discharge event in the current port call. null for events before the first l/d in the port call. */
  operationsStart: number | null;
  end: number;
  /** Whether there was cargo on board BEFORE starting the current event = if there was a leg, was it a laden leg */
  laden: boolean;
  /** The number of orders loaded AFTER the event  */
  ordersOnBoard: number;
  /** 0..1, the ratio for how much of the planned amount was actually loaded/discharged */
  amount: number;
  /** Cargo mass change due to the event */
  mass: number;
  /** Cargo volume change due to the event */
  volume: number;
  /** Cargo volume stowed on hold, 0 for deck cargo */
  holdVolume: number;
  /** Cargo volume stowed on deck, 0 for hold cargo */
  deckVolume: number;
  /** Loaded pieces change due to the event */
  pcs: number;
  previousEventStart: number | null;
  /** Hours. */
  hoursAtSea: number;
  /** Hours. Included in hoursAtSea. */
  channelDurationOverhead: number;
  /** Milliseconds. */
  calculatedSeaDuration: number;
  hoursAtPort: number;
  totalHours: number;
  portActivities: number;
  hoursDelayConstraint: number;
  hoursWaitingOrRtaBuffer: number;
  /** Hours. Schedule violates a contract. Violation relative to port call ETA. Positive when late, negative when early. */
  cancellingOrDeliveryViolationVsEta: number;
  /** Hours. Schedule violates a contract. Violation relative to first load/discharge start in port call. Positive when late, negative when early. */
  cancellingOrDeliveryViolationVsOperations: number;
  /** Hours. Schedule violates a contract. Violation relative to load/discharge start. Positive when late, negative when early. */
  cancellingOrDeliveryViolationVsEvent: number;
  /** Milliseconds. Schedule violates physical reality (i.e. vessel must be in two places at a time). Positive values are bad. */
  physicalTimeConflict?: number;
  /** Milliseconds. Start override violates physical reality. Positive values are bad. */
  startOverrideConflict?: number;
  costs: {
    bunkerGrade: {
      portData?: EnrichedSpeedConsumption;
      portDataSecondary?: EnrichedSpeedConsumption;
      insideEcaData?: EnrichedSpeedConsumption;
      insideEcaDataSecondary?: EnrichedSpeedConsumption;
      outsideEcaData?: EnrichedSpeedConsumption;
      outsideEcaDataSecondary?: EnrichedSpeedConsumption;
      extraConsumptionData?: EnrichedSpeedConsumption;
      extraConsumptionDataSecondary?: EnrichedSpeedConsumption;
      total: number;
    };
    tcCostSea: number;
    tcCostPort: number;
    tcCost: number;
    totalCost: number;
    portCostEstimate: number;
    /** @deprecated Use channel cost from co2Processor if possible */
    channelCost: number;
    cleaningCost?: number;
  };
  totalCO2: number;
  totalConsumedBunkers: number; // consumed bunker in tonnes
  event: EventType;
  distance: number;
  usedChannels: string[];
  bannedChannelsInRoute: string[];
  /** Available vessel mass capacity after the event */
  availableMassCapacity: number;
  /** Available vessel volume capacity after the event */
  availableVolumeCapacity: number;
  /** Available vessel deck volume capacity after the event */
  availableDeckVolumeCapacity: number;
  /** Used vessel mass capacity after the event */
  usedMassCapacity: number;
  /** Used vessel volume capacity after the event */
  usedVolumeCapacity: number;
  /** Used vessel deck volume capacity after the event */
  usedDeckVolumeCapacity: number;
  duration: number;
  /** Hours, absolute value of delay relative to loading/discharging window */
  delays: number;
  /** Hours, delays, but constrained by a maximum */
  constrainedDelays: number;
  /** Hours, signed value of delay relative to loading/discharging window. Positive when late, negative when early. */
  signedDelays: number;
  key: string;
  assortmentExclusions: AssortmentExclusionItem[];
  /** Source values for timings */
  inputs: {
    loadingRate?: {
      value: number | null;
      unit?: LoadingUnit | null;
      source?: LoadingRateSource | null;
    };
    dischargeRate?: {
      value: number | null;
      unit: LoadingUnit | null;
      source: LoadingRateSource | null;
    };
  };
};

export const isTCOrderEventTimeData = <T extends Events>(
  td: TimeData<T | TCOrderEvent>,
): td is TimeData<TCOrderEvent> => isTCOrderEvent(td.event);

export const isSingleEventTimeData = <T extends Events>(
  e: TimeData<T | SingleEvent>,
): e is TimeData<SingleEvent> => isSingleEvent(e.event);

export const isOffHireEventTimeData = <T extends Events>(
  e: TimeData<T | OffHireEvent>,
): e is TimeData<OffHireEvent> => isOffHireEvent(e.event);

export const isDryDockOffHireTimeData = (e: TimeData): boolean =>
  isOffHireEventTimeData(e) && e.event.entry.type === OffHireType.DRY_DOCK;

export const isRepairSingleEventTimeData = (e: TimeData): boolean =>
  isSingleEventTimeData(e) &&
  'event' in e.event &&
  e.event.event === SingleEventType.REPAIR;

/** Matches events that represent a dry dock period (either OffHire DRY_DOCK or SingleEvent REPAIR from IMOS) */
export const isDryDockEventTimeData = (e: TimeData): boolean =>
  isDryDockOffHireTimeData(e) || isRepairSingleEventTimeData(e);

/**
 * Check if we should stop backward expansion at a dry dock boundary.
 * Returns true if the current event is a dry dock, OR if the previous event is a dry dock.
 */
export function shouldStopAtDryDockBoundary(
  timeData: readonly TimeData[],
  currentIndex: number,
): boolean {
  if (currentIndex < 0 || currentIndex >= timeData.length) return false;
  if (isDryDockEventTimeData(timeData[currentIndex])) return true;
  if (currentIndex > 0 && isDryDockEventTimeData(timeData[currentIndex - 1])) {
    return true;
  }
  return false;
}

export const isSpotEventTimeData = <T extends Events>(
  e: TimeData<T | SpotEvent>,
): e is TimeData<SpotEvent> => isSpotEvent(e.event);

export const isCoaEventTimeData = <T extends Events>(
  e: TimeData<T | CoaEvent>,
): e is TimeData<CoaEvent> => isCoaEvent(e.event);

export const isCargoEventTimeData = <T extends Events>(
  e: TimeData<T | CargoEvent>,
): e is TimeData<CargoEvent> => isCargoEvent(e.event);

export const isLoadEventTimeData = <T extends Events>(
  e: TimeData<T | LoadEvent>,
): e is TimeData<LoadEvent> => isCargoEventTimeData(e) && isLoadEvent(e.event);

export const isDischargeEventTimeData = <T extends Events>(
  e: TimeData<T | DischargeEvent>,
): e is TimeData<DischargeEvent> =>
  isCargoEventTimeData(e) && isDischargeEvent(e.event);

export const isPortCallEventTimeData = <T extends Events>(
  e: TimeData<T | PortCallEvent>,
): e is TimeData<PortCallEvent> => isPortCallEvent(e.event);

export const isStaticEventTimeData = <T extends Events>(
  e: TimeData<T | StaticEvent>,
): e is TimeData<StaticEvent> => isStaticEvent(e.event);

export const reverseTimeDataTypeGuard =
  <I extends Events, O extends I>(f: (x: TimeData<I>) => x is TimeData<O>) =>
  (x: TimeData<I>): x is TimeData<Exclude<I, O>> =>
    !f(x);

export const isNotOrderEventTimeData = reverseTimeDataTypeGuard(
  isTCOrderEventTimeData,
);
export const isNotSingleEventTimeData = reverseTimeDataTypeGuard(
  isSingleEventTimeData,
);
export const isNotOffHireEventTimeData = reverseTimeDataTypeGuard(
  isOffHireEventTimeData,
);
export const isNotSpotEventTimeData =
  reverseTimeDataTypeGuard(isSpotEventTimeData);
export const isNotCoaEventTimeData =
  reverseTimeDataTypeGuard(isCoaEventTimeData);
export const isNotPortCallEventTimeData = reverseTimeDataTypeGuard(
  isPortCallEventTimeData,
);
export const isNotStaticEventTimeData = reverseTimeDataTypeGuard(
  isStaticEventTimeData,
);

/** Determines whether the given timedata/event should be associated with a shipment */
export function isNonShipmentEventTimeData(td: TimeData): boolean {
  return isStaticEventTimeData(td) || isDryDockEventTimeData(td);
}

export const getTimedataDurations = (
  td: TimeData | Leg | WaitEvent,
): [EstimatedDuration, CurrentDuration] => {
  const actualDuration = td.end - td.start;
  return [td.calculatedDuration ?? actualDuration, actualDuration];
};

export type TimeDataTotals = {
  totalHours: number;
  hoursAtSea: number;
  hoursAtPort: number;
  ballastDistance: number;
  bunkerCost: number;
  portCost: number;
  distance: number;
  /** Hours */
  delays: number;
  constrainedDelays: number;
  cost: number;
  tcCost: number;
  mass: number;
};

export const initTotals = (): TimeDataTotals => ({
  ballastDistance: 0,
  bunkerCost: 0,
  portCost: 0,
  cost: 0,
  tcCost: 0,
  delays: 0,
  constrainedDelays: 0,
  distance: 0,
  mass: 0,
  totalHours: 0,
  hoursAtPort: 0,
  hoursAtSea: 0,
});

export type TimeDataInputs = {
  vesselId: ShipId;
  tcRate: number;
  weightCapacity: number;
  volumeCapacity: number;
  deckVolumeCapacity: number;
};

export type TcTimeDataGroup = {
  timeData: TimeData<
    TCOrderEvent | SingleEvent | OffHireEvent | PortCallEvent | StaticEvent
  >[];
  inputs: TimeDataInputs;
  totals: TimeDataTotals;
};

export type CoaTimeDataGroup = {
  key: string;
  coaId: CoaContractId;
  vesselId: ShipId | null;
  timeData: TimeData<CoaEvent | PortCallEvent>[];
  inputs: TimeDataInputs;
  totals: TimeDataTotals;
};

export type SpotTimeDataGroup = {
  key: string;
  shipownerOrgId: OrganizationId | null;
  vesselId: ShipId | null;
  timeData: TimeData<SpotEvent | PortCallEvent>[];
  inputs: TimeDataInputs;
  totals: TimeDataTotals;
};
export interface PortCallEventBase extends Event {
  category: EventCategory.PortCall;
  // Most resembles single events for rendering and all. Change to its own type later if needed.
  type: Extract<CoreEventType, CoreEventType.Single>;
  entry: PortCallEntry;
  portCallId: ClientId;
  /** From database, used to check data such as SoF */
  portCall: ScenarioTimeline_PortCallFragment | null;
  event: PortCallEventType;
  location: ScenarioLocation;
  locationId: LocationId;
  locationName: string;
  locationTimeZone: string | null;
  /** Number of shipments associated with the port call, only shipments that are loaded/discharged during the port call are counted */
  shipmentCount: number;
  skipOnSwap: true;
  isCanalTransitPortCall: boolean;
}

export type PortCallArrivalEvent = PortCallEventBase & {
  event: PortCallEventType.Arrival;
  totalLoadDurationOverride: number | null;
  totalDischargeDurationOverride: number | null;
  legAvoidsChannels: boolean;
  channelCostDefaultId: ChannelCostEstimateId | null;
  oneTimeOverrideChannelCost: number | null;
  oneTimeOverrideCost: number | null;
  portCostDefaultId: PortCostDefaultId | null;
  useHistoricalPortCost: boolean;
  useLatestHistoricalPortCost: boolean;
  useOneTimeOverrideCost: boolean;
  useIntegrationPortCost: boolean;
  channelDurationOverhead: number | null;
  previousLocationId: LocationId | null;
  weatherFactor: number | null;
};

export type PortCallBerthingEvent = PortCallEventBase & {
  event: PortCallEventType.Berthing;
};

export type PortCallDepartureEvent = PortCallEventBase & {
  event: PortCallEventType.Departure;
};

export type PortCallEvent =
  | PortCallArrivalEvent
  | PortCallBerthingEvent
  | PortCallDepartureEvent;

export const bunkerGradeDetailsSchema = z.array(
  z.object({
    area: z.enum(VesselArea),
    // This field is only used if the area is overridden to something other than the default area. For example, we are in OUTSIDE_ECA, but we want to use ALL_AREA.
    overrideArea: z.enum(VesselArea).optional().nullable(),
    type: z.enum(VesselSpeedType),
    tag: z.enum(VesselSpeedTag).optional().nullable(),
    bunker_grade_id: z.number(),
  }),
);

export type BunkerGradeDetails = z.infer<typeof bunkerGradeDetailsSchema>;

export const isPortCallArrivalEvent = (e: Events): e is PortCallArrivalEvent =>
  isPortCallEvent(e) && e.event === PortCallEventType.Arrival;

export const isPortCallBerthingEvent = (
  e: Events,
): e is PortCallBerthingEvent =>
  isPortCallEvent(e) && e.event === PortCallEventType.Berthing;

export const isPortCallDepartureEvent = (
  e: Events,
): e is PortCallDepartureEvent =>
  isPortCallEvent(e) && e.event === PortCallEventType.Departure;

export type Events =
  | TCOrderEvent
  | SingleEvent
  | OffHireEvent
  | SpotEvent
  | CoaEvent
  | PortCallEvent
  | StaticEvent;

export enum ImplicitEventType {
  IDLE,
}

export type WaitEvent = Pick<Leg, 'start' | 'end' | 'index'> &
  Pick<SingleEvent, 'locationName' | 'locationTimeZone'> & {
    isGenerated: true;
    eventType: ImplicitEventType;
    calculatedDuration: undefined;
    category: EventCategory.Wait;
    event: {
      locationId: LocationId;
      portCallId: ClientId | null;
    };
    key: string;
  };

export type EventOrLeg<EventType extends Events = Events> =
  | Leg
  | TimeData<EventType>
  | WaitEvent;

export type EventsAndLegs<EventType extends Events = Events> =
  EventOrLeg<EventType>[];

export type Event = {
  category: EventCategory;
  start: number;
  duration: number;
  end: number;
  vesselId: ShipId | null;
  vessel: Vessel | undefined;
  seaDurationOverride: number | null;
  startOverride: Constraint;
  /** Used to make timeline computation wait until earliest loading/discharging. Preceded by startOverride if given.
   * Intended primarily for suggestions computation (as of 2022-12-14). */
  delayUntil?: number;
  /** New: not inserted into scenario yet.
   * Used to flag events which are created as part of tentative assignments during voyage suggestions.
   * This flag affects certain details of used capacity computation. (tentative assignments never assume they fit compartments without allocations) */
  isNew?: boolean;
  constraints: OrderConstraints;
  durationOverride?: number | null | undefined;
  cleaningAssortmentExclusionId?: AssortmentExclusionId | null;
  sofCommenced?: number | null | undefined;
  sofDuration?: number | null | undefined;
  skipOnSwap?: boolean;
  portCallId: ClientId | null;
  /** true when there is a total duration override on the portcall that affects this event */
  portCallControlled?: boolean;
};

export interface TCOrderEvent extends Event {
  category: EventCategory.TC;
  type: Extract<CoreEventType, CoreEventType.Load | CoreEventType.Discharge>;
  concurrent: boolean;
  entry: TCOrderEntry;
  order: ScenarioOrder;
  cargoAllocations: NewCargoAllocation[];
  location: ScenarioLocation;
  locationId: LocationId;
  locationTimeZone: string | null;
  pcs: number;
  durationOverride: number | null;
  vesselId: ShipId;
  vessel: Vessel;
}
export interface SingleEvent extends Event {
  category: EventCategory.Single;
  type: Extract<CoreEventType, CoreEventType.Single>;
  concurrent: boolean;
  locationId: LocationId;
  entry: SingleEntry;
  location: ScenarioLocation;
  locationName: string;
  locationTimeZone: string | null;
  event: SingleEventType;
  durationOverride?: undefined;
  vesselId: ShipId;
  vessel: Vessel;
}
export interface OffHireEvent extends Event {
  category: EventCategory.OffHire;
  type: Extract<CoreEventType, CoreEventType.OffHire>;
  entry: OffHireEntry;
  locationA: ScenarioLocation;
  locationB: ScenarioLocation;
  locationAId: LocationId;
  locationBId: LocationId;
  locationAName: string;
  locationBName: string;
  locationATimeZone: string | null;
  locationBTimeZone: string | null;
  durationOverride?: undefined;
  vesselId: ShipId;
  vessel: Vessel;
  portCallId: null;
}

export interface SpotEvent extends Event {
  category: EventCategory.Spot;
  type: Extract<CoreEventType, CoreEventType.Load | CoreEventType.Discharge>;
  concurrent: boolean;
  entry: SpotEntry;
  order: ScenarioOrder;
  cargoAllocations?: undefined;
  location: ScenarioLocation;
  locationId: LocationId;
  locationTimeZone: string | null;
  pcs: number;
  durationOverride: number | null;
}

export interface CoaEvent extends Event {
  category: EventCategory.COA;
  type: Extract<CoreEventType, CoreEventType.Load | CoreEventType.Discharge>;
  concurrent: boolean;
  entry: CoaEntry;
  order: ScenarioOrder;
  cargoAllocations?: undefined;
  location: ScenarioLocation;
  locationId: LocationId;
  locationTimeZone: string | null;
  pcs: number;
  durationOverride: number | null;
}

type StaticEventType = 'TC_IN';

interface StaticEventBase extends Event {
  category: EventCategory.Static;
  portCallId: null;
  staticEventType: StaticEventType;
  type: CoreEventType.Static;
}

/**
 * TCInEvent represents the time when the vessel is *not* under contract
 * It's start is either the end of previous TC In period or -Infinity
 * It's end is either the start of next TC In period or Infinity
 */
export type TcInEvent = StaticEventBase & {
  entry: TcInEntry;
  staticEventType: 'TC_IN';
  /** Redelivery location id */
  locationAId: LocationId;
  /** Redelivery location name */
  locationAName: string;
  /** Redelivery location timezone */
  locationATimezone: string;
  /** Delivery location id */
  locationBId: LocationId;
  /** Delivery location name */
  locationBName: string;
  /** Delivery location timezone */
  locationBTimezone: string;
};

/**
 * Static events affect calculations but should not be managed by scenario
 */
export type StaticEvent = TcInEvent;

export type Leg = {
  category: EventCategory.Leg;
  start: number;
  from: ScenarioLocation;
  to: ScenarioLocation;
  end: number;
  index: number;
  distance: number;
  hoursAtSea: number; // h
  seaDurationOverride: number | null;
  channelDurationOverhead: number | null; // h, included in hoursAtSea
  /** Weather factor (%) read from the arrival event; null when not applicable. */
  weatherFactor: number | null;
  avoidsChannels: boolean;
  laden: boolean;
  calculatedDuration: number; // ms
  event: {
    locationAId: LocationId;
    locationBId: LocationId;
    portCallId: Event['portCallId'];
  }; // Port call id of the TO location
  key: string;
  usedChannels: string[];
  bannedChannelsInRoute: string[];
};
/**
 * Common interface for load/discharge events on any timeline type (tc/coa/spot)
 */

export type CargoEvent = TCOrderEvent | SpotEvent | CoaEvent;
export const isTimeData = <T extends Events>(
  e: EventOrLeg<T>,
): e is TimeData<T> => 'start' in e && 'isFirstEvent' in e;

export const isNotTimeData = <T extends Events>(
  e: EventOrLeg<T>,
): e is Exclude<EventOrLeg<T>, TimeData<T>> => !isTimeData(e);

export const isLeg = (e: EventOrLeg): e is Leg =>
  'category' in e && e.category === EventCategory.Leg;

export const isWait = (e: EventOrLeg): e is WaitEvent =>
  'category' in e && e.category === EventCategory.Wait;

export const isPortCallArrivalEventTimeData = (
  td: TimeData,
): td is TimeData<PortCallArrivalEvent> =>
  isPortCallEvent(td.event) && td.event.event === PortCallEventType.Arrival;

export const isPortCallBerthingEventTimeData = (
  td: TimeData,
): td is TimeData<PortCallBerthingEvent> =>
  isPortCallEvent(td.event) && td.event.event === PortCallEventType.Berthing;

export const isPortCallDepartureEventTimeData = (
  td: TimeData,
): td is TimeData<PortCallDepartureEvent> =>
  isPortCallEvent(td.event) && td.event.event === PortCallEventType.Departure;

/**
 * Filter out dry dock periods and their adjacent port call events.
 * Handles both OffHire DRY_DOCK and SingleEvent REPAIR (from IMOS).
 *
 * This function excludes ALL dry dock periods, including any that might occur
 * "during" a voyage. This is correct behavior because:
 * - Vessels cannot transport cargo while in dry dock (requires empty vessel)
 * - Dry dock periods should not contribute to voyage P&L or TCE calculations
 */
export function excludeDryDockPeriods(events: TimeData[]): TimeData[] {
  const indicesToExclude = new Set<number>();

  for (let i = 0; i < events.length; i++) {
    if (!isDryDockEventTimeData(events[i])) continue;

    indicesToExclude.add(i);

    // Find the start (arrival) of this dry dock port call.
    // Stop at a Departure event — that belongs to the previous voyage
    let arrivalIndex = i;
    for (let j = i - 1; j >= 0; j--) {
      if (isPortCallEventTimeData(events[j])) {
        if (isPortCallDepartureEventTimeData(events[j])) break;
        indicesToExclude.add(j);
        if (isPortCallArrivalEventTimeData(events[j])) {
          arrivalIndex = j;
          break;
        }
      } else break;
    }

    // Find the end (departure) of this dry dock port call.
    // Stop at an Arrival event — that marks the start of the next voyage's
    // port call, not the end of the dry dock's own.
    const dryDockPortCallId = isPortCallEventTimeData(events[arrivalIndex])
      ? events[arrivalIndex].event.portCallId
      : null;
    for (let j = i + 1; j < events.length; j++) {
      if (isPortCallEventTimeData(events[j])) {
        if (isPortCallArrivalEventTimeData(events[j])) break;
        indicesToExclude.add(j);
        if (isPortCallDepartureEventTimeData(events[j])) break;
      } else if (
        dryDockPortCallId !== null &&
        events[j].event.portCallId === dryDockPortCallId
      ) {
        // Non-port-call event sharing the dry dock's port call — step over
        // it so the forward walk can still reach the shared Departure.
        continue;
      } else break;
    }

    // Check if the preceding port call contains any single event
    if (
      arrivalIndex > 0 &&
      isPortCallDepartureEventTimeData(events[arrivalIndex - 1])
    ) {
      const departureIndex = arrivalIndex - 1;
      const portCallIndices: number[] = [];
      let hasSingleEvent = false;

      for (let j = departureIndex; j >= 0; j--) {
        if (
          !isPortCallEventTimeData(events[j]) &&
          !isSingleEventTimeData(events[j])
        ) {
          break;
        }

        portCallIndices.push(j);

        if (isSingleEventTimeData(events[j])) {
          hasSingleEvent = true;
        }

        if (isPortCallArrivalEventTimeData(events[j])) {
          break;
        }
      }

      // Exclude the entire previous port call if it contains any single event
      if (hasSingleEvent) {
        portCallIndices.forEach((idx) => indicesToExclude.add(idx));
      }
    }
  }

  if (indicesToExclude.size === 0) return events;

  return events.filter((_, i) => !indicesToExclude.has(i));
}

export const getAllShipmentIdsInEvents = (
  tds: readonly TimeData[],
): Set<ClientId> => {
  return new Set(
    filteredMap(tds, (td) =>
      isCargoEventTimeData(td) && td.event.order.shipmentId
        ? td.event.order.shipmentId
        : NONE,
    ),
  );
};

export const getEventStartLocationId = (e: Events): LocationId =>
  'locationAId' in e ? e.locationAId : e.locationId;

export const getEventEndLocationId = (e: Events): LocationId =>
  'locationBId' in e ? e.locationBId : e.locationId;

export const getEventLocationId: (
  event: { locationAId: LocationId } | { locationId: LocationId },
) => LocationId = getEventStartLocationId;

export type TCTimelineEvent =
  | TCOrderEvent
  | SingleEvent
  | OffHireEvent
  | PortCallEvent
  | StaticEvent;

export const isTCTimelineEvent = (x: Events): x is TCTimelineEvent => {
  return (
    isTCOrderEvent(x) ||
    isSingleEvent(x) ||
    isOffHireEvent(x) ||
    isPortCallEvent(x) ||
    isStaticEvent(x)
  );
};

export const hasOrder = <T extends Events>(
  x: T,
): x is T & { order: ScenarioOrder } => 'order' in x;

export const isCargoEvent = <T extends Events>(
  e: T,
): e is Extract<T, CargoEvent> =>
  e.type === CoreEventType.Load || e.type === CoreEventType.Discharge;

export const isCargoEntry = (e: Entry): e is CargoEntry => 'orderId' in e;

export const isScheduleCargoEvent = (
  e: TCScheduleEntry | CoaEntry | SpotEntry,
): e is TCOrderEntry | CoaEntry | SpotEntry => 'orderId' in e;
export type CalculatedWork = {
  /** Actual hours of wall time  */
  duration: number;
  /** Effective hours worked <= duration */
  loadingTimeInHours: number;
  /** (Parts of) Shifts used to do the work */
  workshifts: WorkShift[];
};

export type EnrichedSpeedConsumption = {
  /** Vessel bunker consumption in tons per hour */
  consumption: number;
  fuelCost: number;
  referenceGrade: string;
  /** Duration of event in hours */
  duration: number;
  /** Distance in nautical miles */
  distance: number;
  /** Total consumed bunker during the event in tons */
  consumedBunker: number;
  voyageCost: number;
  idleHours: number;
  gradeId: number;
  gradeName?: string;
  carbonFactor?: number;
  /**
   * Determines which area the data was calculated for.
   * For sea consumption this is ALL_AREAS if route override was used as overrides
   * do not distinguish between inside/outside ECA
   */
  targetArea: VesselArea;
};

export type LoadingRateSource =
  | 'order'
  | 'voyage_charter'
  | 'coa'
  | 'assortment'
  | 'vessel'
  | 'port'
  | 'port_commodity';

export const isTCOrderEvent = (e: Events): e is TCOrderEvent =>
  e.category === EventCategory.TC;
export const isSingleEvent = (e: Events): e is SingleEvent =>
  e.category === EventCategory.Single;
export const isOffHireEvent = (e: Events): e is OffHireEvent =>
  e.category === EventCategory.OffHire;
export const isSpotEvent = (e: Events): e is SpotEvent =>
  e.category === EventCategory.Spot;
export const isCoaEvent = (e: Events): e is CoaEvent =>
  e.category === EventCategory.COA;
export const isPortCallEvent = <T extends Events>(
  e: T,
): e is Extract<T, PortCallEvent> => e.category === EventCategory.PortCall;
export const isStaticEvent = (e: Events): e is StaticEvent =>
  e.category === EventCategory.Static;

export type LoadEvent = (TCOrderEvent | CoaEvent | SpotEvent) & {
  type: CoreEventType.Load;
};
export const isLoadEvent = (e: Events): e is LoadEvent =>
  (isTCOrderEvent(e) || isCoaEvent(e) || isSpotEvent(e)) &&
  e.type == CoreEventType.Load;

export type DischargeEvent = (TCOrderEvent | CoaEvent | SpotEvent) & {
  type: CoreEventType.Discharge;
};
export const isDischargeEvent = (e: Events): e is DischargeEvent =>
  (isTCOrderEvent(e) || isCoaEvent(e) || isSpotEvent(e)) &&
  e.type == CoreEventType.Discharge;

export function isTcInEvent(e: Events): e is TcInEvent {
  return isStaticEvent(e) && e.staticEventType === 'TC_IN';
}

export const canEventBeConcurrent = (e: Events): boolean => {
  return 'concurrent' in e;
};

export const isEventConcurrent = (e: Events): boolean => {
  return 'concurrent' in e && e.concurrent;
};

export type EstimatedDuration = number;
export type CurrentDuration = number;
export enum CoreEventType {
  Load,
  Discharge,
  Single,
  OffHire,
  Static,
}

export enum SingleEventType {
  MANEUVERING,
  BUNKERING,
  CLEANING,
  DRIFTING,
  WAITING,
  CONGESTION,
  PASSING,
  REPAIR,
  CANAL_TRANSIT,
  OTHER,
}

export enum PortCallEventType {
  Arrival = 'Arrive',
  Berthing = 'Berth',
  Departure = 'Depart',
}

export type OffHireEventType = OffHireType;
export type OrderConstraints = {
  layday: Constraint;
  cancelling: Constraint;
  rta: Constraint;
  deliveredBefore: Constraint;
};
export type WorkShift = {
  day: number;
  fromHours: number;
  fromMinutes: number;
  toHours: number;
  toMinutes: number;
};
export type TCOrderEvents = { load: TCOrderEvent; discharge: TCOrderEvent };

type OrderDetails = ScenarioOrder['orderDetailsByOrderId']['nodes'];

export const isDeckCargo = (orderDetails?: OrderDetails) =>
  orderDetails?.some((od) => od?.cargoDeckOption === 'DECK') ?? false;
